export type ShiftLifecycleStatus =
  | 'completed'
  | 'in-progress'
  | 'ongoing'
  | 'upcoming'
  | 'missed'

export interface ShiftLifecycleSource {
  from?: string | null
  to?: string | null
  clock_in_status?: boolean | null
  clock_out_status?: boolean | null
  status?: string | null
}

export interface ShiftLifecycleStats {
  total: number
  completed: number
  inProgress: number
  ongoing: number
  upcoming: number
  missed: number
}

export const getShiftTimestamp = (value?: string | null) => {
  if (!value) return null

  const timestamp = new Date(value).getTime()
  return Number.isNaN(timestamp) ? null : timestamp
}

export const getShiftLifecycleStatus = (
  shift: ShiftLifecycleSource,
  now: Date = new Date()
): ShiftLifecycleStatus => {
  const normalizedStatus = shift.status?.toLowerCase()

  if (shift.clock_out_status || normalizedStatus === 'completed') {
    return 'completed'
  }

  if (shift.clock_in_status) {
    return 'in-progress'
  }

  const shiftStart = getShiftTimestamp(shift.from)
  const shiftEnd = getShiftTimestamp(shift.to)

  if (shiftStart === null || shiftEnd === null) {
    return 'upcoming'
  }

  const currentTime = now.getTime()

  if (shiftEnd < currentTime) {
    return 'missed'
  }

  if (shiftStart <= currentTime && currentTime <= shiftEnd) {
    return 'ongoing'
  }

  return 'upcoming'
}

export const canClockInForShiftStatus = (status: ShiftLifecycleStatus) =>
  status === 'upcoming' || status === 'ongoing'

export const canClockOutForShiftStatus = (status: ShiftLifecycleStatus) =>
  status === 'in-progress'

export const getRelevantShift = <TShift extends ShiftLifecycleSource>(
  shifts: TShift[],
  now: Date = new Date()
) => {
  if (!shifts || shifts.length === 0) return null

  const sortedShifts = [...shifts].sort(
    (a, b) =>
      (getShiftTimestamp(a.from) ?? 0) - (getShiftTimestamp(b.from) ?? 0)
  )

  const findFirstByStatus = (status: ShiftLifecycleStatus) => {
    const shift = sortedShifts.find(
      item => getShiftLifecycleStatus(item, now) === status
    )

    return shift ? { shift, status } : null
  }

  return (
    findFirstByStatus('in-progress') ||
    findFirstByStatus('ongoing') ||
    findFirstByStatus('upcoming') ||
    (() => {
      const missedShift = [...sortedShifts]
        .reverse()
        .find(item => getShiftLifecycleStatus(item, now) === 'missed')

      return missedShift
        ? { shift: missedShift, status: 'missed' as const }
        : null
    })()
  )
}

export const getShiftLifecycleStats = <TShift extends ShiftLifecycleSource>(
  shifts: TShift[],
  now: Date = new Date()
): ShiftLifecycleStats => {
  return shifts.reduce<ShiftLifecycleStats>(
    (stats, shift) => {
      const status = getShiftLifecycleStatus(shift, now)

      stats.total += 1

      switch (status) {
        case 'completed':
          stats.completed += 1
          break
        case 'in-progress':
          stats.inProgress += 1
          break
        case 'ongoing':
          stats.ongoing += 1
          break
        case 'upcoming':
          stats.upcoming += 1
          break
        case 'missed':
          stats.missed += 1
          break
      }

      return stats
    },
    {
      total: 0,
      completed: 0,
      inProgress: 0,
      ongoing: 0,
      upcoming: 0,
      missed: 0,
    }
  )
}
